import React from "react";
import {
  MdAccessTime,
  MdCheckCircle,
  MdEdit,
  MdEmail,
  MdOpenInNew,
  MdQrCode,
  MdTouchApp,
  MdTrendingUp,
} from "react-icons/md";
import AlertPane from "components/alert";
import Badge from "components/badge/Badge";
import Button from "components/button";
import Card, { CardHeader } from "components/card";
import StatCard from "components/stat-card";
import { EmptyState } from "components/states";
import { getDeliveryMeta } from "components/heartbeat/deliveryMethod";
import { formatDateTime, formatNumber } from "utils/format";
import { formatDuration, formatRate, formatRatio } from "./metrics";
import QuestionAnalyticsPanel from "./QuestionAnalyticsPanel";
import SurveyAnalysisCard from "./SurveyAnalysisCard";

const STAT_GRID = "grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4";

const formatHour = (hour) => {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

const DetailRow = ({ label, children, valueClassName = "" }) => (
  <div className="flex items-center justify-between gap-4">
    <dt className="text-fg-muted">{label}</dt>
    <dd className={`text-right font-medium text-fg ${valueClassName}`.trim()}>{children}</dd>
  </div>
);

const ContentBlock = ({ label, children }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-fg-muted">{label}</dt>
    <dd className="mt-1 text-fg">{children}</dd>
  </div>
);

const CampaignDetailsCard = ({ campaign, qrStats }) => {
  const delivery = getDeliveryMeta(campaign.deliveryMethod);
  const DeliveryIcon = delivery.icon;
  const questionCount = campaign.questions?.length || 0;

  return (
    <Card padding="md">
      <CardHeader title={delivery.isQr ? "QR Code / Link Details" : "Campaign Details"} />
      <dl className="mt-4 space-y-3">
        <DetailRow label="Delivery Method">
          <span className="inline-flex items-center gap-2">
            <DeliveryIcon aria-hidden="true" className="h-4 w-4 text-fg-muted" />
            {delivery.label}
          </span>
        </DetailRow>

        {delivery.isQr ? (
          <>
            <DetailRow label="Survey Type" valueClassName="capitalize">
              {campaign.surveyType || "spiritual"}
            </DetailRow>
            <DetailRow label="Questions">{formatNumber(questionCount)}</DetailRow>
            <DetailRow label="Total Scans">{formatNumber(qrStats?.totalScans || 0)}</DetailRow>
            <DetailRow label="Completions">
              {formatNumber(qrStats?.conversions || qrStats?.convertedScans || 0)}
            </DetailRow>
            <DetailRow label="Created">{formatDateTime(campaign.createdAt)}</DetailRow>
          </>
        ) : (
          <>
            <DetailRow label="Recipient Mode" valueClassName="capitalize">
              {campaign.recipientMode || "automatic"}
            </DetailRow>
            {campaign.recipientMode === "automatic" && (
              <DetailRow label="Trigger">
                {campaign.triggerType === "first_checkin" ? "First Check-in" : "Member Creation"}
              </DetailRow>
            )}
            <DetailRow label="Delivery Window">
              {formatHour(campaign.deliveryWindowStart || 9)} -{" "}
              {formatHour(campaign.deliveryWindowEnd || 20)}
            </DetailRow>
            <DetailRow label="Questions">{formatNumber(questionCount)}</DetailRow>
            <DetailRow label="Last Updated">{formatDateTime(campaign.updatedAt)}</DetailRow>
          </>
        )}

        {campaign.isSystem && (
          <DetailRow label="Type">
            <Badge tone="info">System Campaign</Badge>
          </DetailRow>
        )}
      </dl>
    </Card>
  );
};

const SurveyContentCard = ({ campaign }) => {
  const hasContent = campaign.description || campaign.welcomeMessage || campaign.surveyDescription;

  return (
    <Card padding="md">
      <CardHeader title="Survey Content" />
      {hasContent ? (
        <dl className="mt-4 space-y-4">
          {campaign.description && (
            <ContentBlock label="Internal Description">{campaign.description}</ContentBlock>
          )}
          {campaign.welcomeMessage && (
            <ContentBlock label="Welcome Message">{campaign.welcomeMessage}</ContentBlock>
          )}
          {campaign.surveyDescription && (
            <ContentBlock label="Survey Description">{campaign.surveyDescription}</ContentBlock>
          )}
        </dl>
      ) : (
        <EmptyState
          variant="inline"
          title="No content configured. Edit the campaign to add a welcome message and description."
        />
      )}
    </Card>
  );
};

const OverviewTab = ({
  campaign,
  stats,
  statsLoading,
  responsesCount,
  analytics,
  qrStats,
  onConfigure,
  questionAnalyticsProps,
  surveyAnalysisProps,
}) => {
  const isQr = stats?.isQrCampaign;
  const sent = stats?.sent || 0;
  const completed = stats?.completed ?? responsesCount;

  const analyticsQr = analytics?.isQrCampaign;
  // Email: Sent → Opened → Completed. QR: Link Clicks → Started → Completed.
  const reached = (analyticsQr ? analytics?.totalScans : analytics?.totalSent) || 0;
  const engaged = (analyticsQr ? analytics?.uniqueSessions : analytics?.totalOpened) || 0;
  const finished = analytics?.totalCompleted || 0;
  const engagedNotFinished = Math.max(0, engaged - finished);

  return (
    <div className="flex flex-col gap-5">
      {campaign.isSystem && !campaign.isActive && (
        <AlertPane tone="info" title="How to Enable This Campaign" className="">
          <p>This is a system campaign provided by Atrium. Follow these steps to activate it:</p>
          <ol className="mt-3 list-inside list-decimal space-y-2">
            <li>Review the survey questions in the "Questions" tab</li>
            <li>Customize the email template in "Delivery" settings (select "Edit Campaign")</li>
            <li>Set your preferred delivery time window</li>
            <li>Select "Edit Campaign" and toggle the campaign to Active</li>
          </ol>
          <Button
            variant="secondary"
            size="sm"
            className="mt-4"
            leftIcon={<MdEdit aria-hidden="true" />}
            onClick={onConfigure}
          >
            Configure Campaign
          </Button>
        </AlertPane>
      )}

      <div className={STAT_GRID}>
        <StatCard
          icon={isQr ? MdQrCode : MdEmail}
          accent="sky"
          label={isQr ? "Link Clicks" : "Surveys Sent"}
          value={formatNumber(sent)}
          info={
            isQr
              ? "Times the QR code was scanned or the survey link was opened."
              : "Surveys delivered to recipients by email or text."
          }
          loading={statsLoading}
        />
        <StatCard
          icon={MdTouchApp}
          accent="gold"
          label={isQr ? "Started" : "Opened"}
          value={formatNumber(stats?.opened || 0)}
          info={
            isQr ? "Unique visitors who began the survey." : "Recipients who opened the survey."
          }
          loading={statsLoading}
        />
        <StatCard
          icon={MdCheckCircle}
          accent="sage"
          label="Completed"
          value={formatNumber(completed)}
          info="Surveys that were submitted."
          loading={statsLoading}
        />
        <StatCard
          icon={MdTrendingUp}
          accent="slate"
          label={isQr ? "Conversion Rate" : "Response Rate"}
          value={formatRate(completed, sent)}
          caption={formatRatio("Completed", completed, sent, isQr ? "link clicks" : "sent")}
          info={
            isQr
              ? "Share of link clicks that ended in a submitted survey."
              : "Share of sent surveys that were submitted."
          }
          loading={statsLoading}
        />
      </div>

      {analytics && (
        <div className={STAT_GRID}>
          <StatCard
            icon={MdOpenInNew}
            accent="sage"
            label={analyticsQr ? "Start Rate" : "Open Rate"}
            value={formatRate(engaged, reached)}
            caption={
              analyticsQr
                ? formatRatio("Started", engaged, reached, "link clicks")
                : formatRatio("Opened", engaged, reached, "sent")
            }
            info={
              analyticsQr
                ? "Share of link clicks where the visitor began the survey."
                : "Share of sent surveys that recipients opened."
            }
          />
          <StatCard
            icon={MdTrendingUp}
            accent="danger"
            label={analyticsQr ? "Started, not finished" : "Opened, not finished"}
            value={formatRate(engagedNotFinished, engaged)}
            caption={formatRatio(
              "",
              engagedNotFinished,
              engaged,
              analyticsQr ? "who started" : "who opened"
            )}
            info={
              analyticsQr
                ? "Share of visitors who began the survey but did not submit it."
                : "Share of recipients who opened the survey but did not submit it."
            }
          />
          <StatCard
            icon={MdCheckCircle}
            accent="sky"
            label="Completion Rate"
            value={formatRate(finished, analyticsQr ? engaged : reached)}
            caption={
              analyticsQr
                ? formatRatio("Completed", finished, engaged, "started")
                : formatRatio("Completed", finished, reached, "sent")
            }
            info={
              analyticsQr
                ? "Share of visitors who began the survey and submitted it."
                : "Share of sent surveys that were submitted."
            }
          />
          <StatCard
            icon={MdAccessTime}
            accent="slate"
            label="Avg. Survey Time"
            value={formatDuration(analytics.avgTimeToComplete)}
            info="Average time from starting the survey to submitting it."
          />
        </div>
      )}

      <QuestionAnalyticsPanel {...questionAnalyticsProps} />

      <SurveyAnalysisCard {...surveyAnalysisProps} />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <CampaignDetailsCard campaign={campaign} qrStats={qrStats} />
        <SurveyContentCard campaign={campaign} />
      </div>
    </div>
  );
};

export default OverviewTab;
